<template>
  <SidebarMenu class="plant-history-sidebar" :show="showNavSidebar" @showChanged="showNavSidebar = $event">
    <div class="plant-history-sidebar__header border">
      <h3>Filters & Actions</h3>
    </div>

    <div class="plant-history-sidebar__content">
      <div class="sort-wrapper">
        <DropDownList
          class="sort-dropdown"
          :textField="'text'"
          :data-items="sortOptions"
          :modelValue="selectedSortOption"
          @change="onSortChanged($event?.value)"
          data-cy="sort-dropdown"
        />
        <div class="sort-dir" @click="onSortAscendingChanged(!sortAscending)">
          <SortIcon :ascending="sortAscending" fill="grey" activeFill="#399CD3" />
        </div>
      </div>

      <hr class="section-divider" />

      <!-- Filters -->
      <PlantHistoryFilters
        ref="filtersRef"
        :cachePrefix="cachePrefix"
        @apply="onApplyFilters($event)"
        @clear="onResetFilters"
      />

      <hr class="section-divider" />

      <TableSearch
        class="history-search"
        :value="searchInput"
        placeholder="Search by Comment, Visit ID or Job ID"
        @searchSubmitted="onSearchSubmitted($event)"
        data-cy="history-search"
      />

      <ToggleSwitch
        class="my-visits-toggle"
        :modelValue="myVisitsOnly"
        @update:modelValue="onMyVisitsOnlyChanged($event as boolean)"
        text="My Visits Only"
      />
    </div>

    <hr class="section-divider" />

    <div class="plant-history-sidebar__header">
      <h3>Quick actions</h3>
    </div>
    <div class="plant-history-sidebar__actions">
      <IconButton label="EIR Visit List" icon="fa-arrow-left" @click="onBackToEirVisitList" />
      <IconButton label="My Plants" icon="fa-arrow-left" @click="onBackToMyPlants" />
    </div>
  </SidebarMenu>
</template>

<script setup lang="ts">
import { ref } from "vue";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import SortIcon from "@/icons/SortIcon.vue";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import PlantHistoryFilters from "./PlantHistoryFilters.vue";
import IconButton from "@/shared/ui/inputs/IconButton.vue";
import SidebarMenu from "@/shared/ui/layout/SidebarMenu.vue";

const props = defineProps<{
  searchInput: string;
  selectedSortOption: { text: string; value: string };
  sortAscending: boolean;
  myVisitsOnly: boolean;
  cachePrefix: string;
  sortOptions: { text: string; value: string }[];
}>();

const emit = defineEmits<{
  (e: "searchSubmitted", value: string): void;
  (e: "sortAscendingChanged", value: boolean): void;
  (e: "myVisitsOnlyChanged", value: boolean): void;
  (e: "sortChanged", value?: { text: string; value: string }): void;
  (e: "applyFilters", filters: any): void;
  (e: "resetFilters"): void;
}>();

const filtersRef = ref<InstanceType<typeof PlantHistoryFilters>>();
const showNavSidebar = ref(false);

defineExpose({
  filtersRef,
});

const onBackToEirVisitList = function () {
  window.location.href = "../inspections/RfrVisitsSearch?type=Inspection&menu=VisitsAsRfr";
};

const onBackToMyPlants = function () {
  window.location.href = "../RfrAssignments";
};

const onSearchSubmitted = function (value: string) {
  showNavSidebar.value = false;
  emit("searchSubmitted", value);
};

const onSortChanged = function (option?: { text: string; value: string }) {
  showNavSidebar.value = false;
  emit("sortChanged", option);
};

const onSortAscendingChanged = function (value: boolean) {
  showNavSidebar.value = false;
  emit("sortAscendingChanged", value);
};

const onMyVisitsOnlyChanged = function (value: boolean) {
  showNavSidebar.value = false;
  emit("myVisitsOnlyChanged", value);
};

const onApplyFilters = function (filters: any) {
  showNavSidebar.value = false;
  emit("applyFilters", filters);
};

const onResetFilters = function () {
  showNavSidebar.value = false;
  emit("resetFilters");
};
</script>

<style lang="scss" scoped>
.plant-history-sidebar {
  &__header {
    margin-bottom: 20px;

    &.border {
      border-bottom: 2px solid #0056b3;
      padding-bottom: 8px;
    }

    h3 {
      margin: 0;
      color: #333;
    }
  }

  .section-divider {
    margin: 20px 0;
    border: 0;
    border-top: 1px solid #eee;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .section-divider {
      margin: 8px 0;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    > * {
      flex: 1;
      flex-basis: calc(50% - 4px);
    }
  }

  .sort-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;

    .sort-dropdown {
      flex: 1;
    }

    .sort-dir {
      cursor: pointer;
      padding: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ccc;
      border-radius: 4px;
      height: 38px;
      width: 38px;

      &:hover {
        background-color: #f8f9fa;
      }
    }
  }
}

:deep(.history-search) {
  width: 100%;
}

:deep(.my-visits-toggle) {
  margin-top: 5px;
}
</style>
